/**
 * Cross-platform "something changed" SSE channel.
 *
 * A nudge channel pings when data the caller shows may have moved; the ping
 * carries no data and consumers re-fetch their source of truth on receipt.
 * SSE is best-effort (a pod restart or a dropped broadcast loses pings), so a
 * nudge is ONLY a freshness hint: pair it with a refetch on focus/foreground.
 *
 * Pings are debounced (default 1s trailing) because one user action can fan
 * out several events, and each would otherwise trigger its own refetch.
 * My Work (/me/work/events) and the location dashboard
 * (/locations/:id/events) are both built on this.
 */
import { useCallback, useEffect, useRef } from 'react';
import { useSSEChannel, isReactNative, GenericWebSSEClient, GenericMobileSSEClient, } from './useSSEChannel';
export function useNudgeSSE(options) {
    const { baseUrl, path, changeType, logPrefix, authToken, getAuthToken, enabled = true, onNudge, debounceMs = 1000, onConnectionStateChange, } = options;
    const onNudgeRef = useRef(onNudge);
    useEffect(() => {
        onNudgeRef.current = onNudge;
    }, [onNudge]);
    const debounceTimer = useRef(null);
    useEffect(() => () => {
        if (debounceTimer.current)
            clearTimeout(debounceTimer.current);
    }, []);
    const handleEvent = useCallback((event) => {
        if (event.type !== changeType)
            return;
        if (debounceTimer.current)
            clearTimeout(debounceTimer.current);
        debounceTimer.current = setTimeout(() => {
            debounceTimer.current = null;
            onNudgeRef.current();
        }, debounceMs);
    }, [changeType, debounceMs]);
    // A 401/403 will not heal by retrying, and a nudge channel is background
    // plumbing, so lifecycle logging stays quiet.
    const createClient = useCallback((clientOptions) => {
        const behavior = { logPrefix, stopOnAuthError: true, verboseLogging: false, warnOnMissingAuthToken: false };
        return isReactNative()
            ? new GenericMobileSSEClient(clientOptions, behavior)
            : new GenericWebSSEClient(clientOptions, behavior);
    }, [logPrefix]);
    const url = baseUrl && path ? `${baseUrl}${path}` : null;
    const channel = useSSEChannel({
        url,
        authToken,
        getAuthToken,
        enabled: enabled && Boolean(url),
        onEvent: handleEvent,
        onConnectionStateChange,
        createClient,
        hookLabel: logPrefix,
    });
    return {
        connectionState: channel.connectionState,
        isConnected: channel.isConnected,
        reconnect: channel.reconnect,
        disconnect: channel.disconnect,
    };
}
